import React, { useState } from 'react';
import { ChevronLeft, MapPin, X, Star, TrendingUp, TrendingDown, Award, Phone, Navigation, User } from 'lucide-react';

export default function BaseDetailPage({ base, onBack, onSell, getBaseImageUrl, onViewMaterial }) {
  const [introExpanded, setIntroExpanded] = useState(false);
  const [trendItem, setTrendItem] = useState(null);
  const [showModeModal, setShowModeModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [activeTab, setActiveTab] = useState('procurement');

  const procurementList = [
    {
      id: 1,
      name: '小型加工废钢Q2',
      trend: 'up',
      trendText: '近期上涨',
      maxPrice: '2,666.00',
      reward: '+99',
      estimateQty: '200',
      spec: '20mm厚度,宽度≤600mm,长度≤1200mm...',
      image: getBaseImageUrl(1),
      demandType: 'open',
    },
    {
      id: 2,
      name: '中型加工废钢Q1',
      trend: 'down',
      trendText: '近期下跌',
      maxPrice: '2,666.00',
      reward: '+5',
      estimateQty: '200',
      spec: '20mm厚度,宽度≤600mm,长度≤1200mm...',
      image: getBaseImageUrl(2),
      demandType: 'closed',
      deliveryDeadline: '2026-05-30',
      fixedWeight: 80,
    },
    {
      id: 3,
      name: '重型废钢HMS1',
      trend: 'up',
      trendText: '近期上涨',
      maxPrice: '2,666.00',
      reward: '+99',
      estimateQty: '200',
      spec: '20mm厚度,宽度≤600mm,长度≤1200mm...',
      image: getBaseImageUrl(3),
      demandType: 'open',
    },
    {
      id: 4,
      name: '一级破碎料',
      trend: 'up',
      trendText: '近期上涨',
      maxPrice: '2,666.00',
      reward: '+99',
      estimateQty: '200',
      spec: '20mm厚度,宽度≤600mm,长度≤1200mm...',
      image: getBaseImageUrl(4),
      demandType: 'closed',
      deliveryDeadline: '2026-06-15',
      fixedWeight: 50,
    },
    {
      id: 5,
      name: '打包料',
      trend: 'up',
      trendText: '近期上涨',
      maxPrice: '2,666.00',
      reward: '+99',
      estimateQty: '200',
      spec: '20mm厚度,宽度≤600mm,长度≤1200mm...',
      image: getBaseImageUrl(5),
      demandType: 'open',
    },
  ];

  const baseIntro =
    '马钢诚兴金属资源有限公司中心基地是中国宝武欧冶链金与安徽诚兴科技环保有限公司、安徽昕源集团有限公司共同合资成组建。主要从事废钢铁采购、加工、仓储及配送服务。';

  return (
    <div className="bg-slate-50 min-h-full pb-24">
      <div className="sticky top-0 bg-white/95 backdrop-blur-sm z-10 px-4 py-3 border-b border-slate-200 flex items-center shadow-sm">
        <button onClick={onBack} className="p-1 -ml-2">
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <span className="font-bold text-slate-800 ml-2 text-lg">基地详情</span>
      </div>

      <div className="h-56 bg-gradient-to-br from-slate-200 to-slate-300 relative overflow-hidden">
        <img src={getBaseImageUrl(base?.id || 1)} alt={base?.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-amber-500 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Star size={12} className="text-white fill-white" />
              <span className="text-white text-xs font-bold">4.8</span>
            </div>
            <div className="bg-blue-500 px-2 py-0.5 rounded-full">
              <span className="text-white text-xs font-bold">优质基地</span>
            </div>
          </div>
          <h2 className="text-xl font-bold text-white">{base?.name || '马钢诚兴基地'}</h2>
        </div>
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs text-slate-600 font-medium">
          1/6
        </div>
      </div>

      <div className="px-4 py-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <MapPin size={18} className="text-blue-500 shrink-0" />
              <span className="text-sm text-slate-600 truncate">{base?.address || '安徽省马鞍山市花山区慈湖高新区水成路'}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              <button className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium text-sm">
                <Navigation size={14} />
                <span>导航</span>
              </button>
              <button className="flex items-center gap-1 px-3 py-2 border border-blue-400 text-blue-500 rounded-lg font-medium text-sm">
                <span className="text-base">+</span>
                <span>关注</span>
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
            <h3 className="font-bold text-slate-800">基地简介</h3>
          </div>
          <div className="text-sm text-slate-600 leading-relaxed">
            {introExpanded ? baseIntro : `${baseIntro.slice(0, 60)}...`}
            <button onClick={() => setIntroExpanded(!introExpanded)} className="text-blue-500 ml-1 font-medium">
              {introExpanded ? '收起' : '展开'}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-400 to-blue-600 rounded-full" />
            <h3 className="font-bold text-slate-800">联系信息</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                <User size={18} className="text-blue-500" />
              </div>
              <div>
                <div className="text-xs text-slate-500">联系人</div>
                <div className="text-sm font-medium text-slate-800">王经理</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center">
                <Phone size={18} className="text-emerald-500" />
              </div>
              <div>
                <div className="text-xs text-slate-500">联系电话</div>
                <div className="text-sm font-medium text-slate-800">138-0555-8888</div>
              </div>
              <a href="tel:13805558888" className="ml-auto px-3 py-1.5 bg-emerald-500 text-white text-xs rounded-lg font-medium">
                拨打
              </a>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-4 overflow-hidden">
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveTab('procurement')}
              className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'procurement' ? 'text-blue-600' : 'text-slate-500'
              }`}
            >
              采购需求
              <span className="ml-1 text-xs text-slate-400">({procurementList.length})</span>
              {activeTab === 'procurement' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`flex-1 py-3 text-sm font-medium transition-colors relative ${
                activeTab === 'reviews' ? 'text-blue-600' : 'text-slate-500'
              }`}
            >
              基地评价
              <span className="ml-1 text-xs text-slate-400">(128)</span>
              {activeTab === 'reviews' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
              )}
            </button>
          </div>

          {activeTab === 'procurement' ? (
            <div className="p-4">
              <div className="space-y-3">
                {procurementList.map((item) => (
                  <div key={item.id} className="flex gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => onViewMaterial(item)}>
                    <div className="w-24 h-24 bg-slate-200 rounded-lg overflow-hidden shrink-0">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>

                    <div className="flex-1 min-w-0 flex flex-col">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-slate-800 text-sm">{item.name}</h4>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                            item.demandType === 'closed' 
                              ? 'bg-purple-100 text-purple-600' 
                              : 'bg-blue-100 text-blue-600'
                          }`}>
                            {item.demandType === 'closed' ? '闭口' : '敞口'}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTrendItem(item);
                            }}
                            className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${
                              item.trend === 'up'
                                ? 'bg-red-50 text-red-500'
                                : 'bg-emerald-50 text-emerald-500'
                            }`}
                          >
                            {item.trend === 'up' ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                            {item.trendText}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs text-slate-500">最高:</span>
                        <span className="text-red-600 font-bold text-base">{item.maxPrice}</span>
                        <span className="text-xs text-slate-500">元/吨</span>
                      </div>

                      <div className="flex items-center gap-2 mb-2">
                        <Award size={12} className="text-amber-500" />
                        <span className="text-xs text-amber-600 font-medium">限时奖励 {item.reward}元</span>
                      </div>

                      <div className="flex items-center justify-between mt-auto">
                        <div className="text-xs text-slate-500">
                          {item.demandType === 'closed' ? (
                            <>
                              收购量: <span className="text-purple-600 font-medium">{item.fixedWeight}吨（固定）</span>
                            </>
                          ) : (
                            <>
                              预估收购量: <span className="text-slate-700 font-medium">{item.estimateQty}吨</span>
                            </>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMaterial(item);
                            setShowModeModal(true);
                          }}
                          className="px-4 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs rounded-lg font-medium shadow-md hover:shadow-lg transition-shadow"
                        >
                          我要卖
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-4">
              <div className="flex items-center gap-4 mb-4 pb-4 border-b border-slate-100">
                <div className="text-center">
                  <div className="text-3xl font-bold text-slate-800">4.8</div>
                  <div className="flex items-center gap-0.5 mt-1">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} size={12} className={i <= 4 ? 'text-amber-400 fill-amber-400' : i === 5 ? 'text-amber-400 fill-amber-400/50' : 'text-slate-300'} />
                    ))}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">综合评分</div>
                </div>
                <div className="flex-1 space-y-1.5">
                  {[
                    { label: '服务态度', score: 4.9 },
                    { label: '结算速度', score: 4.8 },
                    { label: '价格合理', score: 4.7 },
                    { label: '场地环境', score: 4.6 },
                  ].map(item => (
                    <div key={item.label} className="flex items-center gap-2">
                      <span className="text-xs text-slate-500 w-16">{item.label}</span>
                      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${item.score * 20}%` }} />
                      </div>
                      <span className="text-xs font-medium text-slate-600 w-8">{item.score}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { id: 1, user: '张*明', date: '2025-02-20', content: '服务态度很好，过磅准确，结算及时，下次还会来。', rating: 5 },
                  { id: 2, user: '李*华', date: '2025-02-18', content: '价格合理，场地整洁，工作人员专业。', rating: 5 },
                  { id: 3, user: '王*强', date: '2025-02-15', content: '整体体验不错，就是排队时间有点长。', rating: 4 },
                ].map(review => (
                  <div key={review.id} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center">
                          <span className="text-xs font-medium text-blue-600">{review.user[0]}</span>
                        </div>
                        <span className="text-sm font-medium text-slate-700">{review.user}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map(i => (
                          <Star key={i} size={10} className={i <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'} />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed">{review.content}</p>
                    <div className="text-xs text-slate-400 mt-1.5">{review.date}</div>
                  </div>
                ))}
              </div>

              <button className="w-full mt-3 py-2 text-sm text-blue-500 font-medium hover:text-blue-600 transition-colors">
                查看全部评价 &gt;
              </button>
            </div>
          )}
        </div>
      </div>

      {trendItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-5 py-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500 mb-0.5 font-medium">采购价格趋势</div>
                  <div className="text-base font-bold text-slate-800">
                    {trendItem.name}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`flex items-center gap-1 text-xs px-3 py-1 rounded-full font-bold ${
                      trendItem.trend === 'up'
                        ? 'bg-red-100 text-red-600'
                        : 'bg-emerald-100 text-emerald-600'
                    }`}
                  >
                    {trendItem.trend === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {trendItem.trendText}
                  </span>
                  <button
                    type="button"
                    onClick={() => setTrendItem(null)}
                    className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-300 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-5">
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">最高采购价（含奖励）</div>
                    <div className="text-2xl font-bold text-red-600">
                      {trendItem.maxPrice}
                      <span className="text-sm font-normal text-slate-500 ml-1">元/吨</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500 mb-1">统计周期</div>
                    <div className="text-sm font-bold text-slate-700">近 90 天</div>
                  </div>
                </div>
              </div>

              {(() => {
                const generateSeries = (start, end, days, isUp) => {
                  const series = [];
                  const totalChange = end - start;
                  const baseStep = totalChange / (days - 1);
                  
                  for (let i = 0; i < days; i++) {
                    const basePrice = start + baseStep * i;
                    const wave = Math.sin(i * 0.3) * 30;
                    const noise = (Math.random() - 0.5) * 40;
                    const price = Math.round(basePrice + wave + noise);
                    series.push(price);
                  }
                  return series;
                };
                const series =
                  trendItem.trend === 'up'
                    ? generateSeries(2520, 2666, 90, true)
                    : generateSeries(2666, 2560, 90, false);
                const max = Math.max(...series);
                const min = Math.min(...series);
                const span = max - min || 1;

                return (
                  <div className="mb-4">
                    <div className="flex items-end justify-between h-36 gap-0.5 bg-slate-50 rounded-xl p-3">
                      {series.map((price, idx) => {
                        const ratio = (price - min) / span;
                        const height = 25 + ratio * 75;
                        return (
                          <div
                            key={idx}
                            className="flex flex-col items-center justify-end flex-1 h-full"
                          >
                            <div
                              className={`w-full rounded-t-sm transition-all ${
                                trendItem.trend === 'up'
                                  ? 'bg-gradient-to-t from-red-400 to-red-500'
                                  : 'bg-gradient-to-t from-emerald-400 to-emerald-500'
                              }`}
                              style={{ height: `${height}%` }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-3 px-1">
                      <div className="text-center">
                        <div className="text-xs text-slate-500">最低价</div>
                        <div className="text-sm font-bold text-slate-700">{min} 元/吨</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-slate-500">最高价</div>
                        <div className="text-sm font-bold text-slate-700">{max} 元/吨</div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <div className="flex items-start gap-2">
                  <Award size={16} className="text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-700 leading-relaxed">
                    数据为平台模拟趋势，仅用于展示价格走势效果。实际采购价以当日挂牌价为准，仅供参考。
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModeModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end justify-center">
          <div className="bg-white rounded-t-3xl w-full max-w-md overflow-hidden shadow-2xl animate-slide-up">
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500 mb-0.5 font-medium">选择交易模式</div>
                  <div className="text-lg font-bold text-slate-800">您要如何交易？</div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowModeModal(false);
                    setSelectedMaterial(null);
                  }}
                  className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-300 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-3">
              <button
                onClick={() => {
                  setShowModeModal(false);
                  onSell(selectedMaterial, 'self');
                }}
                className="w-full p-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl text-white shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <User size={24} className="text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-bold text-lg">自己卖</div>
                    <div className="text-sm text-white/80 mt-0.5">使用自己的账户进行交易</div>
                  </div>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowModeModal(false);
                  onSell(selectedMaterial, 'other');
                }}
                className="w-full p-4 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl text-white shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <User size={24} className="text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-bold text-lg">帮别人卖</div>
                    <div className="text-sm text-white/80 mt-0.5">代他人进行废钢交易</div>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}